'use client';

import React, { useMemo, useState, useRef } from 'react';
import { Stock } from '@/types';
import { squarify } from '@/lib/squarify';
import { formatNumber } from '@/lib/formatNumber';
import { useChartColors } from '@/lib/useChartColors';

interface PortfolioTreemapProps {
  stocks: Stock[];
  currency: 'USD' | 'CAD';
  exchangeRate: number;
  onSelectStock: (stock: Stock) => void;
}

interface StockCell {
  stock: Stock;
  value: number;
  weight: number;
  change: number;
}

function getChangeColor(change: number): string {
  // Clamp to -5%..+5% range for interpolation
  const clamped = Math.max(-5, Math.min(5, change));
  const t = (clamped + 5) / 10; // 0 = -5%, 0.5 = 0%, 1 = +5%

  // Red (#dc2626) -> Gray (#374151) -> Green (#16a34a)
  if (t <= 0.5) {
    const s = t / 0.5; // 0..1
    const r = Math.round(220 + (55 - 220) * s);
    const g = Math.round(38 + (65 - 38) * s);
    const b = Math.round(38 + (81 - 38) * s);
    return `rgb(${r},${g},${b})`;
  } else {
    const s = (t - 0.5) / 0.5; // 0..1
    const r = Math.round(55 + (22 - 55) * s);
    const g = Math.round(65 + (163 - 65) * s);
    const b = Math.round(81 + (74 - 81) * s);
    return `rgb(${r},${g},${b})`;
  }
}

export const PortfolioTreemap: React.FC<PortfolioTreemapProps> = ({
  stocks,
  currency,
  exchangeRate,
  onSelectStock,
}) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const chartColors = useChartColors();

  const cells = useMemo<StockCell[]>(() => {
    return stocks
      .filter((s) => s.currentPrice !== null && s.lots.length > 0)
      .map((stock) => {
        const priceInUSD =
          stock.currency === 'CAD'
            ? stock.currentPrice! / exchangeRate
            : stock.currentPrice!;
        const displayPrice =
          currency === 'CAD' ? priceInUSD * exchangeRate : priceInUSD;

        const totalShares = stock.lots.reduce((sum, lot) => sum + lot.shares, 0);
        const value = displayPrice * totalShares;

        // Cost-basis P/L calculation (consistent with StockDetail and StockCard)
        const totalCostUSD = stock.lots.reduce((acc, lot) => {
          const lotCurrency = lot.currency || 'USD';
          const cost = lot.shares * lot.price;
          return acc + (lotCurrency === 'CAD' ? cost / exchangeRate : cost);
        }, 0);
        const currentValueUSD = priceInUSD * totalShares;
        const change = totalCostUSD > 0
          ? ((currentValueUSD - totalCostUSD) / totalCostUSD) * 100
          : 0;

        return { stock, value, weight: 0, change };
      })
      .filter((c) => c.value > 0);
  }, [stocks, currency, exchangeRate]);

  const totalValue = useMemo(
    () => cells.reduce((sum, c) => sum + c.value, 0),
    [cells]
  );

  const cellsWithWeight = useMemo(
    () =>
      cells.map((c) => ({
        ...c,
        weight: totalValue > 0 ? (c.value / totalValue) * 100 : 0,
      })),
    [cells, totalValue]
  );

  // Use percentage-based layout (100x100 grid)
  const rects = useMemo(() => {
    const input = cellsWithWeight.map((c) => ({
      id: c.stock.id,
      value: c.value,
    }));
    return squarify(input, 100, 100);
  }, [cellsWithWeight]);

  const cellMap = useMemo(() => {
    const map = new Map<string, StockCell>();
    cellsWithWeight.forEach((c) => map.set(c.stock.id, c));
    return map;
  }, [cellsWithWeight]);

  const hoveredCell = hoveredId ? cellMap.get(hoveredId) : null;

  if (cells.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p style={{ color: 'var(--color-text-muted)' }}>
          No stock data available for treemap.
        </p>
      </div>
    );
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setContainerWidth(rect.width);
    setTooltipPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const currencySymbol = currency === 'USD' ? '$' : 'C$';

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full"
      onMouseMove={handleMouseMove}
    >
      {rects.map((r) => {
        const cell = cellMap.get(r.id);
        if (!cell) return null;

        const bgColor = getChangeColor(cell.change);
        const minDim = Math.min(r.width, r.height);
        const isHovered = hoveredId === r.id;

        // Use the smaller dimension to determine text size — prevents overflow in narrow cells
        const showTicker = minDim > 3 && r.width > 4;
        const showChange = minDim > 5 && r.width > 6;
        const fontSize =
          minDim > 15 ? 'text-base sm:text-lg' :
          minDim > 10 ? 'text-sm sm:text-base' :
          minDim > 6 ? 'text-xs sm:text-sm' :
          'text-[8px] sm:text-[10px]';
        const changeFontSize =
          minDim > 15 ? 'text-xs sm:text-sm' :
          'text-[8px] sm:text-[10px]';

        return (
          <div
            key={r.id}
            className="absolute cursor-pointer transition-[filter] duration-150"
            style={{
              left: `${r.x}%`,
              top: `${r.y}%`,
              width: `${r.width}%`,
              height: `${r.height}%`,
              padding: '1.5px',
            }}
            onMouseEnter={() => setHoveredId(r.id)}
            onMouseLeave={() => setHoveredId(null)}
            onClick={() => onSelectStock(cell.stock)}
          >
            <div
              className="flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-md"
              style={{
                backgroundColor: bgColor,
                filter: isHovered ? 'brightness(1.25)' : undefined,
              }}
            >
              {showTicker && (
                <span className={`font-bold text-white leading-tight truncate max-w-full px-1 ${fontSize}`}>
                  {cell.stock.symbol}
                </span>
              )}
              {showChange && (
                <span className={`text-white/80 leading-tight truncate max-w-full px-1 ${changeFontSize}`}>
                  {cell.change >= 0 ? '+' : ''}
                  {cell.change.toFixed(1)}%
                </span>
              )}
            </div>
          </div>
        );
      })}

      {/* Tooltip */}
      {hoveredCell && (
        <div
          className="pointer-events-none absolute z-50 rounded-xl border px-3 py-2 text-sm shadow-lg"
          style={{
            left: Math.min(tooltipPos.x + 12, containerWidth > 0 ? containerWidth - 200 : tooltipPos.x + 12),
            top: Math.max(tooltipPos.y - 80, 0),
            backgroundColor: chartColors.tooltipBg,
            borderColor: chartColors.tooltipBorder,
            color: chartColors.textColor,
          }}
        >
          <div className="font-semibold">{hoveredCell.stock.name || hoveredCell.stock.symbol}</div>
          <div className="mt-1 space-y-0.5 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            <div>
              Value: {currencySymbol}
              {formatNumber(hoveredCell.value)}
            </div>
            <div>Portfolio: {hoveredCell.weight.toFixed(1)}%</div>
            <div>
              P/L:{' '}
              <span className={hoveredCell.change >= 0 ? 'text-green-400' : 'text-red-400'}>
                {hoveredCell.change >= 0 ? '+' : ''}
                {hoveredCell.change.toFixed(2)}%
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
